import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, take, takeUntil, tap } from 'rxjs/operators';
import {
  ComponentBase,
  SharedService,
  BusinessDetailsService,
  SavePhasesDataType,
  WORKING_DAY,
} from 'src/app/core';
import { ToastrService } from 'ngx-toastr';
@Component({
  selector: 'app-working-hours-step',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
  ],
  templateUrl: './working-hours-step.component.html',
  styleUrl: './working-hours-step.component.scss',
})
export class WorkingHoursStepComponent extends ComponentBase implements OnInit {
  @Input() showSkip = true;
  @Input() titleKey = 'Set_Your_Business_Hours';
  @Input() descriptionKey: string | undefined;
  @Input() submitLabelKey = 'Confirm';
  @Input() previousLabelKey = 'Previous';
  /** When false, hide internal action buttons (used when embedded in sidenav). */
  @Input() showActions = true;
  /** When true, emit next() on submit/skip success instead of navigating. */
  @Input() sidenavMode = false;

  @Output() next = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  /** Default working hours: 9 AM - 9 PM (for time inputs use HH:mm) */
  readonly defaultStartTime = '09:00';
  readonly defaultEndTime = '21:00';

  form!: FormGroup;
  workingDays: WORKING_DAY[] = [];
  selectAllDays = true;
  startAll: string | undefined = '09:00';
  endAll: string | undefined = '21:00';
  dir = 'ltr';
  private readonly successRoute = 'smart-suggestions';

  /** Loader flag for workingDays while calling getWeekDays */
  workingDaysLoading = false;
  /** Loader flag for Skip request only (so Next isn't affected). */
  skipLoading = false;

  constructor(
    private readonly sharedService: SharedService,
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    readonly translateService: TranslateService
  ) {
    super();
    this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.getWeekDays();
  }

  private initForm(): void {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    this.form = new UntypedFormGroup({
      RegistrationCycleLogId: new UntypedFormControl(regId ? +regId : null),
    });
  }

  private getWeekDays(updateNamesOnly = false): void {
    if (!updateNamesOnly) {
      this.workingDaysLoading = true;
    }
    this.businessDetailsService
      .getWeekDays()
      .pipe(
        take(1),
        tap(res => {
          if (updateNamesOnly) {
            this.workingDays.forEach((day) => {
              const match = res.Result?.find(
                (item) => day.WeekDay === item.WeekDay
              );
              if (match) day.GlobalName = match.GlobalName;
            });
          } else {
            this.workingDays =
              res.Result?.map((item) => ({
                ...item,
                StartTime: this.defaultStartTime,
                FinishTime: this.defaultEndTime,
                checked: true,
              })) ?? [];
          }
        }),
        finalize(() => {
          if (!updateNamesOnly) {
            this.workingDaysLoading = false;
          }
          this.loading = false;
        })
      ).subscribe();
  }

  checkAllFromInput(event: Event): void {
    const checked = (event.target as HTMLInputElement)?.checked ?? false;
    if (checked) {
      const start = this.startAll ?? this.defaultStartTime;
      const end = this.endAll ?? this.defaultEndTime;
      this.workingDays = this.workingDays.map((item) => ({
        ...item,
        checked: true,
        StartTime: start,
        FinishTime: end,
      }));
      this.startAll = start;
      this.endAll = end;
    } else {
      this.workingDays = this.workingDays.map((item) => ({
        ...item,
        checked: false,
        StartTime: this.defaultStartTime,
        FinishTime: this.defaultEndTime,
      }));
      this.startAll = this.defaultStartTime;
      this.endAll = this.defaultEndTime;
    }
  }

  onChangeAllTime(type: string, event: Event): void {
    if (!this.selectAllDays) return;
    const value = (event.target as HTMLInputElement)?.value;
    if (type === 'start') {
      this.startAll = value;
      this.workingDays = this.workingDays.map((item) => ({
        ...item,
        StartTime: value,
      }));
    }
    if (type === 'end') {
      this.endAll = value;
      this.workingDays = this.workingDays.map((item) => ({
        ...item,
        FinishTime: value,
      }));
    }
  }

  onSkip(): void {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId) {
      this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
      this.router.navigate(['registration-cycle']);
      return;
    }
    this.skipLoading = true;
    this.businessDetailsService
      .skip({
        RegistrationCycleLogId: +regId,
        PhaseNumber: 4,
      } as { RegistrationCycleLogId: number; PhaseNumber: number })
      .pipe(
        take(1),
        tap(res => {
          if (this.sharedService.checkResponseStatus(res.Message, res.State)) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            if (this.sidenavMode) this.next.emit();
            else this.router.navigate(['smart-suggestions']);
          }
        }),
        finalize(() => this.skipLoading = false)
      ).subscribe();
  }

  onPrevious(): void {
    this.previous.emit();
  }

  onSubmit(): void {
    const workingDayHour = this.workingDays.filter((item) => item.checked);
    if (
      this.businessDetailsService.checkTimeValidation(workingDayHour) ||
      !workingDayHour?.length
    ) {
      this.toastr.warning(this.translateService.instant('DAY_VALIDATION'));
      return;
    }

    this.loading = true;
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId) {
      this.loading = false;
      this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
      this.router.navigate(['registration-cycle']);
      return;
    }

    this.businessDetailsService
      .saveWorkingHours({
        RegistrationCycleLogId: +regId,
        SavePhasesDataType: SavePhasesDataType.Add,
        WorkingHours: workingDayHour.map((item) => ({
          StartTime: item.StartTime!,
          FinishTime: item.FinishTime!,
          WeekDay: item.WeekDay!,
        })),
      })
      .pipe(
        take(1),
        tap(res => {
          if (this.sharedService.checkResponseStatus(res.Message, res.State)) {
            if (this.sidenavMode) this.next.emit();
            else this.router.navigate(['smart-suggestions']);
          }
        }),
        finalize(() => this.loading = false)
      ).subscribe();
  }
}
